import { enumPreference } from './preferences'

/**
 * How the app looks for this user on this browser (Settings › Appearance), on top of light/dark
 * (theme.ts). Each choice is a `data-*` attribute on <html> that tokens.css and app.css key off, so
 * components never branch on it. Documents printed or previewed as paper ignore all of it.
 */

/**
 * `modern`: a dark side rail, warm paper pages and white panels (the cloudTech apps' look).
 * `nimbus`: glass chrome over a drifting cloud field, luminous accent. `ledger`: the flat paper look.
 */
export const STYLES = ['modern', 'nimbus', 'ledger'] as const
export type AppStyle = (typeof STYLES)[number]

export const ACCENTS = ['jade', 'stamp', 'sky', 'teal', 'saffron', 'rose', 'violet'] as const
export type Accent = (typeof ACCENTS)[number]

export const CORNERS = ['sharp', 'soft', 'round'] as const
export type Corners = (typeof CORNERS)[number]

/** Whether the Nimbus cloud field drifts (it never does when the OS asks for reduced motion). */
export const AMBIENT = ['on', 'off'] as const
export type Ambient = (typeof AMBIENT)[number]

export const DEFAULT_STYLE: AppStyle = 'modern'
export const DEFAULT_ACCENT: Accent = 'jade'

export const stylePreference = enumPreference<AppStyle>('appearance.style', STYLES, DEFAULT_STYLE)
export const accentPreference = enumPreference<Accent>('appearance.accent', ACCENTS, DEFAULT_ACCENT)
export const cornersPreference = enumPreference<Corners>('appearance.corners', CORNERS, 'soft')
export const ambientPreference = enumPreference<Ambient>('appearance.ambient', AMBIENT, 'on')

/** The swatch shown for each accent in the picker (the light-mode ink; tokens.css holds both). */
export const ACCENT_SWATCHES: Record<Accent, string> = {
  jade: '#046650',
  stamp: '#3b3e9e',
  sky: '#0b6aa6',
  teal: '#0c6f69',
  saffron: '#8a5300',
  rose: '#b0265a',
  violet: '#6b35c4',
}

export function applyAppearance(): void {
  const root = document.documentElement
  root.dataset.style = stylePreference.get()
  root.dataset.accent = accentPreference.get()
  root.dataset.corners = cornersPreference.get()
  root.dataset.ambient = ambientPreference.get()
}

export function startAppearanceSync(): () => void {
  applyAppearance()
  const stops = [stylePreference, accentPreference, cornersPreference, ambientPreference].map((p) =>
    p.subscribe(applyAppearance),
  )
  return () => {
    for (const stop of stops) stop()
  }
}

/** Back to how the app looks out of the box. */
export function resetAppearance(): void {
  stylePreference.set(DEFAULT_STYLE)
  accentPreference.set(DEFAULT_ACCENT)
  cornersPreference.set('soft')
  ambientPreference.set('on')
}
